import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { VERIFY_MAGIC_LINK_MUTATION } from '@graphql/mutations/session/magicLinkMutations';
import { useCallback } from 'react';

import type { UserResponse } from '../types';

export type VerifyMagicLinkInput = {
  token: string;
};

export type VerifyMagicLinkResponse = {
  accessToken?: string | null;
  refreshToken?: string | null;
  user?: UserResponse | null;
  errors?: Array<{ code: string; message: string; field?: string }>;
};

export const useVerifyMagicLink = () => {
  const [verifyMagicLinkMutation, { loading: verifyMagicLinkLoading }] = useMutation<{
    verifyMagicLink: VerifyMagicLinkResponse;
  }>(VERIFY_MAGIC_LINK_MUTATION);

  const doVerifyMagicLink = useCallback(
    async (input: VerifyMagicLinkInput): Promise<VerifyMagicLinkResponse> => {
      const response = await verifyMagicLinkMutation({
        variables: { input },
      });
      const result = response.data?.verifyMagicLink;

      if (!result) {
        notify.error({ title: 'Verification failed', description: 'No response from server' });
        throw new Error('Magic link verification failed');
      }

      if (result.errors && result.errors.length > 0) {
        notify.error({ title: 'Verification failed', description: result.errors[0]?.message });
      }

      return result;
    },
    [verifyMagicLinkMutation]
  );

  return { doVerifyMagicLink, verifyMagicLinkLoading };
};
